import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Heart,
  MessageCircle,
  MoreVertical,
  Flag,
  UserX,
  Trash2,
  User,
  Pencil,
  Share2,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import postService, {
  DartPost,
  POST_AUTHOR_TYPE_LABELS,
  formatRelativeTime,
  getImageUrl,
} from '../../services/postService';
import CommentSection from '../comments/CommentSection';
import PostEditor from './PostEditor';
import ConfirmDialog from '../ConfirmDialog';
import usePrompt from '../../hooks/usePrompt';
import { profilePath } from '../../utils/profilePath';
import { t } from '../../i18n';

const ACCENT = '#3fced1';
const CAPTION_LIMIT = 150;

/**
 * Autor-Link: verweist je nach Typ auf die passende öffentliche Detailseite
 * (Routen aus App.tsx). Spieler-Posts verlinken auf das öffentliche
 * Community-Profil (/community/profil/:username).
 */
function buildAuthorLink(post: DartPost): string | null {
  // Spieler: Slug (= Benutzername) oder Fallback auf die User-ID – nie der Anzeigename
  if (post.authorType === 'PLAYER') {
    return profilePath({ username: post.authorSlug, userId: post.authorId });
  }
  if (!post.authorSlug) return null;
  const slug = encodeURIComponent(post.authorSlug);
  switch (post.authorType) {
    case 'CLUB':
      return `/verein/${slug}`;
    case 'PUB':
      return `/spielstaette/${slug}`;
    case 'SHOP':
      return `/shop/${slug}`;
    case 'TOURNAMENT':
      return `/turnier/${slug}`;
    case 'SERIES':
      return `/turnierserie/${slug}`;
    case 'LEAGUE':
      return `/liga/${slug}`;
    case 'ASSOCIATION':
      return `/verband/${slug}`;
    default:
      return null;
  }
}

interface PostCardProps {
  post: DartPost;
  /** Wird nach Löschen / Blockieren aufgerufen, damit die Liste den Post entfernt. */
  onRemoved?: (postId: string) => void;
  /**
   * True auf der Detailseite /beitrag/:id: Klick auf Bild/Caption navigiert
   * dann NICHT (Caption klappt stattdessen per Klick auf); die Kommentare
   * bettet die Detailseite selbst fest ein.
   */
  detailMode?: boolean;
}

/**
 * Instagram-artige Beitrags-Karte: Autor-Kopf, 4:5-Bildbereich
 * (Marcels Regel: festes Seitenverhältnis), Snap-Carousel bei mehreren
 * Bildern, Like (optimistisch mit Rollback), aufklappbare Kommentare,
 * Caption mit "mehr"-Expand und ⋮-Menü (Melden / Blockieren / Löschen).
 * Klick auf Bild oder Caption öffnet die Detailseite /beitrag/:id
 * (außer im detailMode).
 */
const PostCard: React.FC<PostCardProps> = ({ post, onRemoved, detailMode = false }) => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [liked, setLiked] = useState<boolean>(post.likedByMe);
  const [likeCount, setLikeCount] = useState<number>(post.likeCount);
  const [likeBusy, setLikeBusy] = useState(false);

  // 2026-09-03: Kommt vom Elternteil ein frischer Beitrag (z. B. Detailseite
  // lädt nach dem Öffnen neu, Feed-Refresh nach Login), Like-Status und
  // Zähler übernehmen – sonst bleibt das Herz auf dem Stand vom ersten Render.
  useEffect(() => {
    setLiked(post.likedByMe);
    setLikeCount(post.likeCount);
  }, [post.id, post.likedByMe, post.likeCount]);

  const [showComments, setShowComments] = useState(false);
  const [captionExpanded, setCaptionExpanded] = useState(false);
  // Bearbeiten (Marcel 2026-08-31): Vollbild-Editor; nach dem Speichern
  // rendert die Karte den aktualisierten Post (Caption UND Bilder)
  const [editOpen, setEditOpen] = useState(false);
  const [edited, setEdited] = useState<DartPost | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmBlock, setConfirmBlock] = useState(false);

  const [activeImage, setActiveImage] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { prompt, promptDialog } = usePrompt();

  const myId = (user as any)?.id ?? null;
  const isOwn = !!myId && !!post.createdByUserId && post.createdByUserId === myId;
  const authorLink = buildAuthorLink(post);
  const typeLabel = post.authorType ? POST_AUTHOR_TYPE_LABELS[post.authorType] : null;
  const avatarUrl = getImageUrl(post.authorImage);
  const images = edited?.images ?? post.images ?? [];
  const caption = edited?.caption ?? (edited ? '' : post.caption ?? '');
  const isCaptionLong = caption.length > CAPTION_LIMIT;

  const showToast = (text: string) => {
    setToast(text);
    window.setTimeout(() => setToast(null), 4000);
  };

  // Like optimistisch togglen, bei Fehler zurückrollen
  const handleLike = async () => {
    if (!isAuthenticated || likeBusy) return;
    const prevLiked = liked;
    const prevCount = likeCount;
    setLiked(!prevLiked);
    setLikeCount(Math.max(0, prevCount + (prevLiked ? -1 : 1)));
    setLikeBusy(true);
    const result = await postService.toggleLike(post.id);
    if (result) {
      setLiked(result.liked);
      setLikeCount(result.likeCount);
    } else {
      // Rollback
      setLiked(prevLiked);
      setLikeCount(prevCount);
    }
    setLikeBusy(false);
  };

  const handleReport = async () => {
    setMenuOpen(false);
    // Modernes Modal statt window.prompt (Marcel 2026-09-01); Grund optional
    const reason = await prompt({
      title: t('web.component.report-modal.beitrag-melden'),
      label: t('web.component.post-card.warum-moechtest-du-diesen-beitrag'),
      placeholder: t('web.component.post-card.z-b-spam-beleidigender-inhalt'),
      confirmLabel: t('web.component.report-modal.melden'),
      allowEmpty: true,
    });
    if (reason === null) return; // abgebrochen
    const ok = await postService.reportPost(post.id, reason);
    showToast(ok ? t('web.component.post-card.beitrag-wurde-gemeldet-danke') : t('web.component.post-card.melden-fehlgeschlagen-bitte-versuche-es'));
  };

  const performBlock = async () => {
    if (!post.createdByUserId) return;
    const ok = await postService.blockUser(post.createdByUserId);
    if (ok) {
      setHidden(true);
      onRemoved?.(post.id);
    } else {
      showToast(t('web.component.post-card.blockieren-fehlgeschlagen-bitte-versuche'));
    }
  };

  const performDelete = async () => {
    try {
      await postService.deletePost(post.id);
      setHidden(true);
      onRemoved?.(post.id);
    } catch {
      showToast(t('web.component.post-card.loeschen-fehlgeschlagen-bitte-versuche-e'));
    }
  };

  // Teilen (Marcel 2026-08-31): natives Teilen, Fallback Zwischenablage
  const sharePost = async () => {
    const url = `https://dartcloudapp.de/beitrag/${encodeURIComponent(post.id)}`;
    const author = post.authorName ?? t('web.component.footer.dartcloud');
    try {
      if (navigator.share) {
        await navigator.share({ title: 'DartCloud', text: `Beitrag von ${author} auf DartCloud`, url });
        return;
      }
    } catch {
      return; // Nutzer hat das Teilen abgebrochen
    }
    try {
      await navigator.clipboard.writeText(url);
      showToast(t('web.component.post-card.link-kopiert'));
    } catch {
      showToast(t('web.component.registrations-manager.link-konnte-nicht-kopiert-werden'));
    }
  };

  // Bild-/Caption-Klick → Detailseite (nicht im detailMode)
  const goToDetail = () => {
    if (detailMode) return;
    navigate(`/beitrag/${encodeURIComponent(post.id)}`);
  };

  /** Zu Bild [idx] scrollen (Pfeil-Buttons/Dots — Desktop hat kein Swipe). */
  const scrollToImage = (idx: number) => {
    const el = scrollRef.current;
    if (!el) return;
    const clamped = Math.min(Math.max(idx, 0), images.length - 1);
    if (typeof el.scrollTo === 'function') {
      el.scrollTo({ left: clamped * el.clientWidth, behavior: 'smooth' });
    }
    setActiveImage(clamped);
  };

  const onCarouselScroll = () => {
    const el = scrollRef.current;
    if (!el || el.clientWidth === 0) return;
    const idx = Math.round(el.scrollLeft / el.clientWidth);
    if (idx !== activeImage) setActiveImage(Math.min(Math.max(idx, 0), images.length - 1));
  };

  if (hidden) return null;

  const authorHead = (
    <div className="flex items-center gap-3 min-w-0">
      <div className="h-9 w-9 rounded-full bg-zinc-800 overflow-hidden flex items-center justify-center shrink-0">
        {avatarUrl ? (
          <img src={avatarUrl} alt={post.authorName ?? ''} className="h-full w-full object-cover" />
        ) : (
          <User size={16} className="text-zinc-500" />
        )}
      </div>
      <div className="min-w-0">
        <p className="text-sm font-bold text-white truncate">{post.authorName ?? t('web.component.comment-item.unbekannt')}</p>
        <p className="text-[11px] text-zinc-500 truncate">
          {typeLabel}
          {typeLabel && post.createdAt ? ' · ' : ''}
          {formatRelativeTime(post.createdAt)}
        </p>
      </div>
    </div>
  );

  return (
    <article className="w-full" data-testid="post-card">
      {/* Kopf */}
      <div className="flex items-center justify-between gap-2 px-1 pb-2">
        {authorLink ? (
          <Link to={authorLink} className="hover:opacity-80 transition min-w-0">
            {authorHead}
          </Link>
        ) : (
          authorHead
        )}

        {/* Drei-Punkte-Menü */}
        <div className="relative shrink-0">
          <button
            type="button"
            aria-label={t('web.component.post-card.beitrags-optionen')}
            onClick={() => setMenuOpen((v) => !v)}
            className="p-2 rounded-full text-zinc-400 hover:text-white hover:bg-zinc-800 transition"
          >
            <MoreVertical size={18} />
          </button>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
              <div className="absolute right-0 top-10 z-20 w-52 rounded-xl border border-zinc-800 bg-zinc-900 shadow-xl py-1">
                <button
                  type="button"
                  onClick={handleReport}
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-zinc-200 hover:bg-zinc-800 transition"
                >
                  <Flag size={15} style={{ color: ACCENT }} />
                  {t('web.component.report-modal.melden')}
                </button>
                {!isOwn && post.createdByUserId && (
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      setConfirmBlock(true);
                    }}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-zinc-200 hover:bg-zinc-800 transition"
                  >
                    <UserX size={15} style={{ color: ACCENT }} />
                    {t('web.component.post-card.nutzer-blockieren')}
                  </button>
                )}
                {isOwn && (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        setMenuOpen(false);
                        setEditOpen(true);
                      }}
                      className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-zinc-200 hover:bg-zinc-800 transition"
                    >
                      <Pencil size={15} style={{ color: ACCENT }} />
                      {t('web.component.registrations-manager.bearbeiten')}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setMenuOpen(false);
                        setConfirmDelete(true);
                      }}
                      className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-400 hover:bg-zinc-800 transition"
                    >
                      <Trash2 size={15} />
                      {t('web.component.comment-item.loeschen')}
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Bildbereich: IMMER 4:5, object-cover (feste Bild-Formate) */}
      {images.length > 0 && (
        <div className="relative rounded-xl overflow-hidden bg-zinc-900">
          <div
            ref={scrollRef}
            onScroll={onCarouselScroll}
            className={
              images.length > 1
                ? 'flex overflow-x-auto snap-x snap-mandatory scrollbar-hide'
                : 'flex overflow-hidden'
            }
            data-testid="post-carousel"
          >
            {images.map((img, idx) => {
              const url = getImageUrl(img);
              return (
                <div
                  key={`${img}-${idx}`}
                  onClick={goToDetail}
                  className={`w-full shrink-0 snap-center aspect-[4/5] bg-zinc-900${
                    detailMode ? '' : ' cursor-pointer'
                  }`}
                >
                  {url && (
                    <img
                      src={url}
                      alt={t('web.component.post-card.beitragsbild', { idx: idx + 1 })}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      draggable={false}
                    />
                  )}
                </div>
              );
            })}
          </div>

          {/* Blätter-Pfeile (Desktop hat kein Swipe) */}
          {images.length > 1 && activeImage > 0 && (
            <button
              type="button"
              aria-label={t('web.component.image-lightbox.vorheriges-bild')}
              onClick={(e) => {
                e.stopPropagation();
                scrollToImage(activeImage - 1);
              }}
              className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-1.5 text-white backdrop-blur-sm transition hover:bg-black/70"
            >
              <ChevronLeft size={20} />
            </button>
          )}
          {images.length > 1 && activeImage < images.length - 1 && (
            <button
              type="button"
              aria-label={t('web.component.image-lightbox.naechstes-bild')}
              onClick={(e) => {
                e.stopPropagation();
                scrollToImage(activeImage + 1);
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-1.5 text-white backdrop-blur-sm transition hover:bg-black/70"
            >
              <ChevronRight size={20} />
            </button>
          )}

          {/* Punkte-Indikator (klickbar) */}
          {images.length > 1 && (
            <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5">
              {images.map((_, idx) => (
                <button
                  key={idx}
                  type="button"
                  aria-label={t('web.component.post-card.bild-anzeigen', { idx: idx + 1 })}
                  onClick={(e) => {
                    e.stopPropagation();
                    scrollToImage(idx);
                  }}
                  className="h-1.5 w-1.5 rounded-full transition"
                  style={{
                    backgroundColor: idx === activeImage ? ACCENT : 'rgba(255,255,255,0.45)',
                  }}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Aktionsleiste */}
      <div className="flex items-center gap-4 px-1 pt-2.5">
        <button
          type="button"
          aria-label={liked ? t('web.component.live-chat.gefaellt-mir-entfernen') : t('web.component.comment-item.gefaellt-mir')}
          aria-pressed={liked}
          onClick={handleLike}
          disabled={!isAuthenticated}
          className="flex items-center gap-1.5 text-sm text-zinc-300 disabled:opacity-50 transition hover:opacity-80"
        >
          <Heart
            size={22}
            style={liked ? { color: ACCENT, fill: ACCENT } : undefined}
            className={liked ? '' : 'text-zinc-300'}
          />
          <span data-testid="like-count">{likeCount.toLocaleString('de-DE')}</span>
        </button>
        <button
          type="button"
          aria-label={t('web.component.post-card.kommentare-anzeigen')}
          onClick={() => {
            // Im detailMode sind die Kommentare fest unter der Card eingebettet
            if (!detailMode) setShowComments((v) => !v);
          }}
          className="flex items-center gap-1.5 text-sm text-zinc-300 transition hover:opacity-80"
        >
          <MessageCircle size={22} className="text-zinc-300" />
          <span>{post.commentCount.toLocaleString('de-DE')}</span>
        </button>
        {/* Teilen (Marcel 2026-08-31) */}
        <button
          type="button"
          onClick={sharePost}
          aria-label={t('web.component.post-card.beitrag-teilen')}
          className="flex items-center gap-1.5 rounded-full p-1.5 text-sm font-semibold text-zinc-300 hover:text-white transition"
        >
          <Share2 size={21} className="text-zinc-300" />
        </button>
      </div>

      {/* Caption: Klick → Detailseite; im detailMode klappt sie stattdessen auf */}
      {caption.length > 0 && (
        <p
          onClick={() => (detailMode ? setCaptionExpanded(true) : goToDetail())}
          className={`px-1 pt-2 text-sm text-zinc-200 whitespace-pre-wrap break-words${
            detailMode ? '' : ' cursor-pointer'
          }`}
        >
          {authorLink ? (
            <Link
              to={authorLink}
              onClick={(e) => e.stopPropagation()}
              className="font-bold text-white hover:underline"
            >
              {post.authorName ?? t('web.component.comment-item.unbekannt')}
            </Link>
          ) : (
            <span className="font-bold text-white">{post.authorName ?? t('web.component.comment-item.unbekannt')}</span>
          )}{' '}
          {isCaptionLong && !captionExpanded ? `${caption.substring(0, CAPTION_LIMIT)}… ` : caption}
          {isCaptionLong && !captionExpanded && (
            <button
              type="button"
              // Marcel 2026-09-03: „mehr" klappt die Beschreibung IMMER direkt
              // im Feed auf – nie zur Detailseite springen
              onClick={(e) => {
                e.stopPropagation();
                setCaptionExpanded(true);
              }}
              className="text-zinc-500 hover:text-zinc-300 font-medium"
            >
              mehr
            </button>
          )}
        </p>
      )}

      {toast && <p className="px-1 pt-2 text-xs" style={{ color: ACCENT }}>{toast}</p>}

      {/* Kommentare (wiederverwendete CommentSection; im detailMode bettet
          die Detailseite sie selbst fest ein) */}
      {showComments && !detailMode && (
        <div className="mt-4 border-t border-zinc-800 pt-4">
          <CommentSection entityType="post" entityId={post.id} title={t('web.component.post-card.kommentare')} />
        </div>
      )}

      {/* Bestätigung: Beitrag löschen */}
      {/* Vollbild-Editor: Stats + Bilder + Beschreibung */}
      <PostEditor
        post={edited ?? post}
        isOpen={editOpen}
        onClose={() => setEditOpen(false)}
        onSaved={(updated) => setEdited(updated)}
      />

      <ConfirmDialog
        open={confirmDelete}
        title={t('web.component.post-card.beitrag-loeschen')}
        message={t('web.component.post-card.moechtest-du-diesen-beitrag-wirklich')}
        confirmLabel={t('web.component.comment-item.loeschen')}
        danger
        onConfirm={performDelete}
        onClose={() => setConfirmDelete(false)}
      />

      {/* Bestätigung: Nutzer blockieren */}
      <ConfirmDialog
        open={confirmBlock}
        title={t('web.component.post-card.nutzer-blockieren')}
        message={t('web.component.post-card.moechtest-du-wirklich-blockieren-du', { authorName: post.authorName ?? 'diesen Nutzer' })}
        confirmLabel={t('web.component.post-card.blockieren')}
        danger
        onConfirm={performBlock}
        onClose={() => setConfirmBlock(false)}
      />

      {/* Melde-Dialog (moderner window.prompt-Ersatz) */}
      {promptDialog}
    </article>
  );
};

export default PostCard;
